// Which columns the Customers list shows, and in what order.
//
// The same split `ticket-columns.js` makes, for the same reasons: the CATALOGUE is here
// because each column needs a renderer, and the SELECTION is data because an admin
// chooses it. The difference is where the selection lives — tickets keep theirs on a
// saved view, and there are no saved views for customers, so this one comes from
// `customers.default_columns` in `app_settings` (migration 026). That is the whole
// reason that table exists.
//
// The server owns the KEYS (`CUSTOMER_COLUMN_KEYS` in src/services/admin/settings.js),
// this owns the labels, widths and renderers, and test/routes/admin-settings.test.js
// pins the two lists to each other rather than trusting them to stay in step.

/** Every column the list can render, in its default order. */
export const CUSTOMER_COLUMNS = Object.freeze([
  { key: 'name', label: 'Name', width: 'w-[21%]' },
  { key: 'position', label: 'Position', width: 'w-[7.5rem]' },
  { key: 'phone', label: 'Phone', width: 'w-[8.5rem]' },
  { key: 'email', label: 'Email', width: 'w-[21%]' },
  { key: 'organization', label: 'Organization', width: 'w-[12%]' },
  { key: 'store', label: 'Store', width: 'w-[11%]' },
  { key: 'open', label: 'Open', width: 'w-[4.5rem]' },
]);

const BY_KEY = new Map(CUSTOMER_COLUMNS.map((c) => [c.key, c]));

/**
 * The one column that cannot be dropped.
 *
 * Name is what identifies the row and what links to the contact. A list without it is
 * rows of phone numbers with nothing to click, which reads as a broken screen rather
 * than as a deliberate arrangement.
 */
const OPENABLE = ['name'];

/**
 * The columns to render, resolved from the stored arrangement.
 *
 * Empty or absent means "no arrangement", which is the state until an admin sets one and
 * the state a reset returns to — the full catalogue in its default order, so the list an
 * agent already knows is what they keep getting.
 */
export function visibleCustomerColumns(selected) {
  if (!Array.isArray(selected) || selected.length === 0) return [...CUSTOMER_COLUMNS];

  const seen = new Set();
  const chosen = [];
  for (const key of selected) {
    const column = BY_KEY.get(String(key));
    // Unknown keys are dropped rather than rendered as a blank column, so an arrangement
    // saved by a newer build stays usable on an older one.
    if (!column || seen.has(column.key)) continue;
    seen.add(column.key);
    chosen.push(column);
  }

  // An empty result needs no branch of its own: it has nothing openable in it either, so
  // the check below returns the default for it.
  if (!chosen.some((c) => OPENABLE.includes(c.key))) return [...CUSTOMER_COLUMNS];
  return chosen;
}

/**
 * Whether a stored arrangement would actually be honoured, in the admin picker's
 * words. The customers counterpart of `selectionProblem` in ticket-columns.js, and it
 * has to be its own function: the rules differ (Name here, Ref or Subject there), and
 * a picker that told an admin to add a column this list does not have would be worse
 * than saying nothing.
 */
export function customerSelectionProblem(selected) {
  if (!Array.isArray(selected) || selected.length === 0) {
    return { ok: false, reason: 'Nothing selected, so this list uses the default columns.' };
  }
  const known = selected.map(String).filter((key) => BY_KEY.has(key));
  if (known.length === 0) {
    return { ok: false, reason: 'None of these columns exist, so the default is used.' };
  }
  if (!known.some((key) => OPENABLE.includes(key))) {
    return {
      ok: false,
      reason: 'Include Name — without it there is nothing to click through to, so the '
        + 'default columns are used instead.',
    };
  }
  return { ok: true };
}
